/**
 * MED-1355: the one rule that decides whether an address is a Medley email.
 *
 * A coach keeps two addresses:
 * - the Medley email (users.email), at a Medley domain. A group lead and a
 *   1:1 coach need it. The cohort pool does not.
 * - the personal email (preferred_email), never at a Medley domain.
 *
 * This mirrors medley-api utils/medleyEmail.ts. Change both together.
 */
import { EMAIL_REGEX } from './validation';

export const MEDLEY_EMAIL_DOMAINS: readonly string[] = ['withmedley.com'];

/**
 * True when the address is at a Medley domain. The match ignores case and
 * surrounding spaces. A subdomain (mail.withmedley.com) does not count.
 */
export function isMedleyEmail(email: string | null | undefined): boolean {
  if (typeof email !== 'string') return false;
  const parts = email.trim().toLowerCase().split('@');
  if (parts.length !== 2 || !parts[0]) return false;
  return MEDLEY_EMAIL_DOMAINS.includes(parts[1]);
}

/**
 * MED-1355: the Medley email of a coach, or null. An older account can sign
 * in with a non-Medley address in users.email. That address is not the
 * Medley email, so a screen that shows the Medley email shows "Not set" or
 * the "No Medley email" badge for it. Use this on every coach screen.
 */
export function medleyEmailOf(email: string | null | undefined): string | null {
  return isMedleyEmail(email) ? (email as string) : null;
}

const DOMAIN_LIST = MEDLEY_EMAIL_DOMAINS.join(' or ');

// Printable ASCII only (0x21 to 0x7E). Refuses full-width and zero-width
// characters that IDNA could map onto a Medley domain.
const PRINTABLE_ASCII = /^[\x21-\x7e]+$/;
// Domain labels of letters, digits and inner hyphens, with a letter TLD and
// no trailing dot (jane@withmedley.com. still delivers to withmedley.com).
const DOMAIN = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))*\.[a-z]{2,63}$/i;

/**
 * The client copy of the API format rule (medley-api isCoachEmailFormat):
 * printable ASCII, one @, a plain domain with a TLD, and no trailing dot.
 * The API stays the final check.
 */
export function isCoachEmailFormat(value: string): boolean {
  if (!PRINTABLE_ASCII.test(value) || !EMAIL_REGEX.test(value)) return false;
  const at = value.lastIndexOf('@');
  if (at !== value.indexOf('@')) return false;
  return DOMAIN.test(value.slice(at + 1));
}

/**
 * The app copy for the form rules. Each message names the fix in plain words.
 * The API keeps its own messages. The app maps API error codes to a field
 * (coachEmailErrorField). The blank personal email message is the API text,
 * so the form and the API never say two different things for one error.
 */
export const COACH_EMAIL_MESSAGES = {
  // A blank field gets a plain message. The domain rule is for a Medley address.
  // MED-1355 QA round 5: the same words as the API personalRequired message.
  personalRequired: 'Personal email is required. Enter a personal (non-Medley) email.',
  personal: 'This is a Medley address. Put it in Medley Email.',
  // An older Medley address that is still stored in the personal email.
  storedPersonalIsMedley: 'This is a Medley address. Enter a personal email.',
  medleyNotMedley: `This is not a ${DOMAIN_LIST} address. Put it in Personal Email.`,
  invalidFormat: 'Enter a valid email address.',
} as const;

export const MEDLEY_EMAIL_LABEL = 'Medley Email';
export const PERSONAL_EMAIL_LABEL = 'Personal Email (non-Medley)';

/** Form rule for the personal email: required, valid, and not a Medley address. */
export function validatePersonalEmail(value: string | null | undefined): string | null {
  if (!value?.trim()) return COACH_EMAIL_MESSAGES.personalRequired;
  if (!isCoachEmailFormat(value.trim())) return COACH_EMAIL_MESSAGES.invalidFormat;
  if (isMedleyEmail(value)) return COACH_EMAIL_MESSAGES.personal;
  return null;
}

/** Form rule for the Medley email: optional, but when given it must be at a Medley domain. */
export function validateMedleyEmail(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  if (!isCoachEmailFormat(value.trim())) return COACH_EMAIL_MESSAGES.invalidFormat;
  if (!isMedleyEmail(value)) return COACH_EMAIL_MESSAGES.medleyNotMedley;
  return null;
}

/** The one normal form the API stores: trimmed and lowercased. '' when absent. */
export function normalizeEmailForCompare(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/**
 * The coach form field that an API error belongs to, so the form can show
 * the message under that field. Returns null for errors about other things.
 */
export function coachEmailErrorField(error: unknown): 'email' | 'preferred_email' | null {
  if (!(error instanceof Error)) return null;
  const { code, meta } = error as Error & {
    code?: string;
    meta?: { errors?: Array<{ field?: string }> };
  };
  if (code === 'PERSONAL_EMAIL_INVALID' || code === 'PERSONAL_EMAIL_IN_USE') return 'preferred_email';
  if (code === 'MEDLEY_EMAIL_INVALID' || code === 'MEDLEY_EMAIL_IN_USE') return 'email';
  const field = meta?.errors?.[0]?.field;
  if (field === 'email' || field === 'preferred_email') return field;
  return null;
}
